<template>
  <div :data-field-table-scope="scope" class="field-table-container">
  <AntRow ref="fieldTableRootRef">
    <AntCol :span="24">
      <AntTable
        ref="fieldTableRef"
        class="field-table"
        :columns="columns"
        :dataSource="dataSource"
        rowKey="key"
        v-model:expandedRowKeys="expandedRowKeys"
        :pagination="false"
        :scroll="{ x: 'max-content', y: 700 }"
        :customRow="customRow"
        bordered
        expandFixed="right"
        :loading="isTableLoading"
      >
        <template #headerCell="{ title, column }">
          <template v-if="column.dataIndex === 'examples'">
            <div>{{ title }}</div>

            <AntSelect
              class="field-table-example-select"
              :value="selectedExampleId"
              style="width: 100%;"
              :options="exampleOptions"
              :open="exampleSelectOpen"
              :dropdownMatchSelectWidth="false"
              dropdownClassName="field-table-example-select-dropdown"
              :dropdownStyle="exampleSelectDropdownStyle"
              :getPopupContainer="getExampleSelectPopupContainer"
              @focus="() => { exampleSelectOpen = true }"
              @change="onChangeSelectedExample"
              @dropdownVisibleChange="dropdownVisibleChange"
            >
              <template #option="{ value }">
                <div class="example-option-row">
                  <AntInput
                    v-if="editingExampleId === value"
                    ref="exampleInputRef"
                    :value="selectedSectionState.examples[value].exampleName"
                    @mousedown.stop
                    @blur="e => onBlurExampleInput(e.target.value)"
                    @focus="onFocusExampleInput"
                    @pressEnter="e => onBlurExampleInput(e.target.value)"
                    @change="e => onChangeExampleInput(e.target.value, value)"
                    @click.stop
                  />
                  <div
                    v-else
                    class="example-option-label"
                    :title="selectedSectionState.examples[value]?.exampleName"
                  >
                    {{ selectedSectionState.examples[value]?.exampleName }}
                  </div>
                  <div
                    v-if="!editingExampleId || editingExampleId !== value"
                    class="example-icons"
                  >
                    <EditIcon
                      title="Edit"
                      class="edit-icon"
                      @click="e => onClickEditExample(e, value)"
                    />
                    <CopyIcon
                      title="Copy"
                      class="copy-icon"
                      @click="e => onClickCopyExample(e, value)"
                    />
                    <AntPopconfirm
                      :title="`Sure to delete? This action will also remove the same example of ${anotherScope}.`"
                      @confirm="e => onClickDeleteExample(e, value)"
                    >
                      <DeleteIcon
                        v-if="Object.keys(selectedSectionState.examples).length > 1"
                        title="Delete"
                        @click.stop
                      />
                    </AntPopconfirm>
                  </div>
                </div>
              </template>

              <template #dropdownRender="{ menuNode: menu }">
                <VNodes :vnodes="menu" />
                <AntDivider style="margin: 4px 0" />
                <div
                  style="padding: 4px 8px; cursor: pointer"
                  @mousedown="e => e.preventDefault()"
                  @click="onClickAddExample"
                >
                  <AddIcon />
                  Add Example
                </div>
              </template>
            </AntSelect>

            <label class="table-label">Platform:</label>
            <PlatformDocSelect
              type="platforms"
              :value="selectedSectionState?.examples?.[selectedExampleId]?.platforms"
              :options="platformOptions"
              @change="val => {
                selectedSectionState.examples[selectedExampleId].platforms = val // eslint-disable-line vue/no-mutating-props
              }"
            />
          </template>
          <div v-else-if="column.dataIndex === 'fieldName'">
            <div>{{ title }}</div>
            <ul class="docs-setting-desc-list">
              <li>Used In：需顯示在哪本 API 文件中</li>
              <li>Not Used In：表示不顯示在該 API 文件中，當 Used In 為 All 時才可設定</li>
              <li>Version：可切換檢視或新增刪除不同的 API 文件客製內容版本</li>
            </ul>
          </div>
          <span v-else>{{ title }}</span>
        </template>

        <template #bodyCell="{ text, column, record }">
          <template v-if="showSearchText && column.dataIndex === 'userId'">
            <template
              v-for="(fragment, i) in text
                .split(new RegExp(`(?<=${showSearchText})|(?=${showSearchText})`, 'i'))"
            >
              <mark
                v-if="fragment.toLowerCase() === showSearchText.toLowerCase()"
                :key="i"
                class="highlight"
              >
                {{ fragment }}
              </mark>
              <template v-else>
                {{ fragment }}
              </template>
            </template>
          </template>

          <template v-else>
            <!-- <template v-if="['fieldName', 'maxLength'].includes(column.dataIndex)"> -->
            <template v-if="column.dataIndex === 'sort'">
              <DragIcon
                v-if="!record.isNew"
                style="cursor: move; color: #999"
                @mouseenter="onMouseEnterDrag"
                @mouseleave="onMouseLeaveDrag"
              />
            </template>

            <template v-if="column.dataIndex === 'fieldName'">
              <AntTooltip
                :overlayClassName="getEffectiveSelectedDocId(record) ? '' : 'hide-tooltip'"
                title="請切回 General 版本編輯"
                placement="topLeft"
              >
                <AntInput
                  placeholder="Key"
                  :style="`width: calc(100% - ${record.level * 16}px); margin-left: ${record.level * 16}px`"
                  :value="getFieldNameInputValue(record)"
                  @change="e => onChangeFieldNameValue(e.target.value, record)"
                  @blur="() => onBlurFieldNameInput(record)"
                  @pressEnter="e => e?.target?.blur?.()"
                  :disabled="!!getEffectiveSelectedDocId(record)"
                />
              </AntTooltip>

              <div :style="`margin-left: ${(record.level + 1) * 20}px`">
                <div class="docs-setting-row">
                  <label class="table-label">Used In:</label>
                  <AntTooltip
                    :overlayClassName="getEffectiveSelectedDocId(record) ? '' : 'hide-tooltip'"
                    title="請切回 General 版本編輯"
                    placement="topLeft"
                  >
                    <PlatformDocSelect
                      type="docs"
                      :value="getFieldStateKey(record.fieldId, 'docs')"
                      :options="(selectedSectionState.docs || []).map(docId => ({ label: docsDataByDocId[docId].docShortTitle, value: docId }))"
                      @change="val => onChange(val ? sortDocIds(val) : null, 'docs', record)"
                      :disabled="!!getEffectiveSelectedDocId(record)"
                    />
                  </AntTooltip>
                </div>

                <div v-if="!record.docs" class="docs-setting-row">
                  <label class="table-label">Not Used In:</label>
                  <AntTooltip
                    :overlayClassName="customizedVersionSelectedDocIdByFieldId[record.fieldId] ? '' : 'hide-tooltip'"
                    title="請切回 General 版本編輯"
                    placement="topLeft"
                  >
                    <PlatformDocSelect
                      type="excludedDocs"
                      :value="getFieldStateKey(record.fieldId, 'excludedDocs')"
                      :options="(selectedSectionState.docs || []).map(docId => ({ label: docsDataByDocId[docId].docShortTitle, value: docId }))"
                      @change="val => onChange(val ? sortDocIds(val) : null, 'excludedDocs', record)"
                      :disabled="!!getEffectiveSelectedDocId(record)"
                    />
                  </AntTooltip>
                </div>

                <div class="docs-setting-row">
                  <label class="table-label">Version:</label>
                  <CustomizedVersionSelect
                    size="small"
                    :selectedDocId="getEffectiveSelectedDocId(record)"
                    :docsDataByDocId="docsDataByDocId"
                    :selectedSectionState="selectedSectionState"
                    :selectedSectionOrFieldState="fieldState[record.fieldId] || {}"
                    :dropdownStyle="{ width: '500px' }"
                    @update:selectedDocId="val => setSelectedCvDocId(record, val)"
                  />
                </div>
              </div>

              <div
                v-if="isDev"
                style="font-size: 11px; color: #bbb;"
              >
                <div v-if="!record.docId?.includes('master')">docId: {{ record.docId }}</div>
                key: {{ record.key }}<br>
                sortOrder: {{ record.sortOrder }}<br>
                idx: {{ record.idx }}<br>
                level: {{ record.level }}
                <div v-if="record.isNew">isNew: {{ record.isNew }}</div>
                <div v-if="record.added">added: {{ record.added }}</div>
              </div>
            </template>

            <template v-if="column.dataIndex === 'fieldType'">
              <!-- <pre style="font-size: 10px; max-height: 250px; overflow-y: auto;">{{ record }}</pre> -->
              <AntTooltip
                :overlayClassName="getEffectiveSelectedDocId(record) ? '' : 'hide-tooltip'"
                title="請切回 General 版本編輯"
                placement="bottomLeft"
              >
                <AntSelect
                  placeholder="Type"
                  style="width: 100%;"
                  :value="getFieldStateKey(record.fieldId, 'fieldType')"
                  :options="getFeldTypeOptions(record)"
                  show-search
                  @change="val => onChangeFieldType(val, record)"
                  :disabled="!!getEffectiveSelectedDocId(record)"
                />
              </AntTooltip>
            </template>

            <template v-if="column.dataIndex === 'format'">
              <AntSelect
                v-if="getFieldStateKey(record.fieldId, 'fieldType') === 'number'"
                style="width: 100%; margin-bottom: 4px;"
                placeholder="Number Type"
                :value="getFieldStateKey(record.fieldId, 'numberType')"
                :options="numberTypeOptions"
                @change="val => onChange(val, 'numberType', record)"
              />
              <AntInputNumber
                v-if="getFieldStateKey(record.fieldId, 'fieldType') === 'string'"
                style="width: 100%;"
                placeholder="Max Length"
                min="1"
                :value="getFieldStateKey(record.fieldId, 'maxLength')"
                @change="val => onChange(val, 'maxLength', record)"
              />
              <AntInputNumber
                v-if="getFieldStateKey(record.fieldId, 'numberType') === 'float'"
                style="width: 100%;"
                placeholder="Decimal Places"
                min="0"
                :value="getFieldStateKey(record.fieldId, 'decimalPlace')"
                @change="val => onChange(val, 'decimalPlace', record)"
              />
              <div v-if="!['number', 'string'].includes(getFieldStateKey(record.fieldId, 'fieldType'))">
                -
              </div>
            </template>

            <template v-if="column.dataIndex === 'required'">
              <AntSwitch
                v-if="!record.level || ['array of objects', 'object'].includes(getFieldStateKey(record.parentFieldId, 'fieldType'))"
                :checked="getFieldStateKey(record.fieldId, 'required')"
                @change="val => onChange(val, 'required', record)"
              />
              <div v-else>
                -
              </div>
            </template>

            <template v-if="column.dataIndex === 'platforms'">
              <AntTooltip
                :overlayClassName="getEffectiveSelectedDocId(record) ? '' : 'hide-tooltip'"
                title="請切回 General 版本編輯"
                placement="topLeft"
              >
                <PlatformDocSelect
                  type="platforms"
                  :value="getFieldStateKey(record.fieldId, 'platforms')"
                  :options="platformOptions"
                  @change="val => onChange(val, 'platforms', record)"
                  :disabled="!!getEffectiveSelectedDocId(record)"
                />
              </AntTooltip>
            </template>

            <template v-if="column.dataIndex === 'content'">
              <SectionEditor
                height="250px"
                :html="record.level ? findTopParentFieldData(record).fieldType === 'stringified json' : false"
                :previewMode="true"
                :removedToolbarItems="['table', 'image']"
                :imageManagementData="imageManagementData"
                :sectionsDataByDocId="sectionsDataByDocId"
                :customizedVersionSelectedDocId="getEffectiveSelectedDocId(record) || `${selectedSectionState.wallet}#master`"
                :editorContent="getFieldStateKey(record.fieldId, 'content')"
                :onChange="val => onChange(val, 'content', record)"
              />
            </template>

            <template v-if="column.dataIndex === 'examples'">
              <div
                v-if="record.fieldType === 'array of objects'"
                style=""
              >
                <!-- <AntInput
                  v-if="fieldState[record.fieldId][column.dataIndex]?.[selectedExampleId]"
                  placeholder="Example"
                  :value="fieldState[record.fieldId][column.dataIndex]?.[selectedExampleId]"
                  @change="e => onChangeExampleField(e.target.value, record)"
                /> -->
                <div @click="onClickAddArrayItem(record)" class="array-item-action">
                  <AddIcon />
                  Add array item
                </div>
                <div
                  v-show="getFieldStateData(record).examples?.[selectedExampleId]?.arrayLength > 1"
                  class="array-item-actions"
                >
                  <div class="array-item-action">
                    <AntPopconfirm
                      :title="`Sure to remove all array item #${selectedArrayItemIdx + 1} below?`"
                      @confirm="onClickRemoveArrayItem(record)"
                    >
                      <div class="array-item-action">
                        <DeleteIcon />
                        Remove array item #
                      </div>
                    </AntPopconfirm>
                    <AntSelect
                      size="small"
                      :value="selectedArrayItemIdx"
                      :options="Array.from({ length: getFieldStateData(record).examples?.[selectedExampleId]?.arrayLength || 1 }, (_, i) => ({ label: i + 1, value: i }))"
                      @change="onChangeSelectedArrayItemIdx"
                    />
                  </div>
                </div>
              </div>

              <div
                v-else-if="getExampleListForRecord(record)"
                class="array-items"
              >
                <div
                  v-for="(example, idx) in getExampleListForRecord(record)"
                  :key="`${selectedExampleId}-${idx}`"
                  class="array-item"
                >
                  <span class="array-item-index">{{ idx + 1 }}. </span>
                  <div class="example-item-body">
                    <div class="example-item-row">
                      <JsonExampleInput
                        v-if="shouldCheckExampleFormatError(record)"
                        :input-key="`${record.fieldId}:${selectedExampleId}:${idx}`"
                        :value="example"
                        :disabled="!!getEffectiveSelectedDocId(record)"
                        :title="getEffectiveSelectedDocId(record) ? '請切回 General 版本編輯' : undefined"
                        @change="val => onChangeExampleField(val, record, idx)"
                      />
                      <AntTooltip
                        v-else
                        :overlayClassName="getEffectiveSelectedDocId(record) ? '' : 'hide-tooltip'"
                        title="請切回 General 版本編輯"
                        placement="topLeft"
                      >
                        <AntInput
                          placeholder="Example"
                          :value="example"
                          :disabled="!!getEffectiveSelectedDocId(record)"
                          @change="e => onChangeExampleField(e.target.value, record, idx)"
                        />
                      </AntTooltip>
                    </div>
                    <div
                      v-if="shouldCheckExampleFormatError(record)"
                      v-show="isExampleFormatError(example, record, { expectArray: false })"
                      class="ant-form-item-explain-error"
                    >
                      {{ getExampleFormatErrorMessage(record) }}
                    </div>
                  </div>
                </div>
              </div>

              <template v-else-if="!['stringified json', 'object'].includes(record.fieldType)">
                <div class="example-item-body">
                  <div class="example-item-row">
                    <JsonExampleInput
                      v-if="shouldCheckExampleFormatError(record)"
                      :input-key="`${record.fieldId}:${selectedExampleId}:scalar`"
                      :value="getExampleScalarForRecord(record)"
                      :disabled="!!getEffectiveSelectedDocId(record)"
                      :title="getEffectiveSelectedDocId(record) ? '請切回 General 版本編輯' : undefined"
                      @change="val => onChangeExampleField(val, record)"
                    />
                    <AntTooltip
                      v-else
                      :overlayClassName="getEffectiveSelectedDocId(record) ? '' : 'hide-tooltip'"
                      title="請切回 General 版本編輯"
                      placement="topLeft"
                    >
                      <AntInput
                        placeholder="Example"
                        :value="getExampleScalarForRecord(record)"
                        :disabled="!!getEffectiveSelectedDocId(record)"
                        @change="e => onChangeExampleField(e.target.value, record)"
                      />
                    </AntTooltip>
                  </div>
                  <div
                    v-if="shouldCheckExampleFormatError(record)"
                    v-show="isExampleFormatError(getExampleScalarForRecord(record), record, { expectArray: true })"
                    class="ant-form-item-explain-error"
                  >
                    {{ getExampleFormatErrorMessage(record) }}
                  </div>
                </div>
              </template>

              <div v-else>
                -
              </div>
            </template>

            <template v-if="column.dataIndex === 'action'">
              <AntPopconfirm
                v-if="!record.isNew"
                :title="`Sure to delete?`"
                @confirm="onDeleteField(record)"
              >
                <DeleteIcon title="Delete" />
              </AntPopconfirm>
            </template>
          </template>
        </template>
      </AntTable>

      <div
        v-if="isDev"
        style="margin-top: 16px; display: block;"
      >
        <div style="display: flex;">
          <DevDebugDataBlock title="dataSource" :data="dataSource" width="50%" maxHeight="250px" />
        </div>
        <div style="display: flex;">
          <DevDebugDataBlock title="fieldState" :data="fieldState" width="50%" maxHeight="250px" />
        </div>
        <div style="display: flex;">
          <div style="width: 50%;">
            <p><b>editingExampleId:</b> {{ editingExampleId }}</p>
            <p><b>exampleSelectOpen:</b> {{ exampleSelectOpen }}</p>
            <p><b>selectedExampleId:</b> {{ selectedExampleId }}</p>
            <p><b>exampleOptions:</b> {{ exampleOptions }}</p>
          </div>
        </div>
      </div>
    </AntCol>
  </AntRow>
  </div>
</template>

<script>
import {
  h,
  ref,
  reactive,
  inject,
  provide,
  watch,
  computed,
  nextTick,
  getCurrentInstance,
} from 'vue'

import ShortUniqueId from 'short-unique-id'
import { cloneDeep } from 'lodash'

import {
  Row as AntRow,
  Col as AntCol,
  Table as AntTable,
  Input as AntInput,
  InputNumber as AntInputNumber,
  Select as AntSelect,
  Switch as AntSwitch,
  Tooltip as AntTooltip,
  Divider as AntDivider,
  Popconfirm as AntPopconfirm,
  Modal as AntModal,
  Tag as AntTag,
} from 'ant-design-vue'

import AddIcon from '@ant-design/icons-vue/lib/icons/PlusOutlined'
import EditIcon from '@ant-design/icons-vue/lib/icons/EditOutlined'
import CopyIcon from '@ant-design/icons-vue/lib/icons/CopyOutlined'
import DeleteIcon from '@ant-design/icons-vue/lib/icons/DeleteOutlined'
import DragIcon from '@ant-design/icons-vue/lib/icons/HolderOutlined'

import SectionEditor from '@/components/ApiDoc/editor/SectionEditor.vue'
import PlatformDocSelect from '@/components/ApiDoc/shared/PlatformDocSelect.vue'
import DevDebugDataBlock from '@/components/ApiDoc/shared/DevDebugDataBlock.vue'
import CustomizedVersionSelect from '@/components/ApiDoc/editor/CustomizedVersionSelect.vue'
import JsonExampleInput from '@/components/ApiDoc/editor/JsonExampleInput.vue'

import { emptyObject } from '@/utils'
import { sortDocIds } from '../lib/libsV3'

import {
  clearTableRowDragIndicator,
  updateTableRowDragIndicator,
  getDropPositionFromRow,
  reorderRowsWithDropPosition,
} from './tableRowDragSort'

import {
  shouldCheckExampleFormatError,
  getExampleFormatErrorMessage,
  normalizeExampleValueForInput,
  isExampleFormatError,
  findExampleFormatErrorInScopeFields,
  buildExampleFormatInputKey,
  getExampleFormatInputKeyCandidates,
} from './fieldExampleFormatValidation'

import './tableRowDragSort.css'

export default {
  name: 'FieldTable',
  components: {
    DevDebugDataBlock,
    SectionEditor,
    PlatformDocSelect,
    CustomizedVersionSelect,
    JsonExampleInput,
    AntRow,
    AntCol,
    AntTable,
    AntInput,
    AntInputNumber,
    AntSelect,
    AntSwitch,
    AntTooltip,
    AntDivider,
    AntPopconfirm,
    AddIcon,
    EditIcon,
    CopyIcon,
    DeleteIcon,
    DragIcon,
    VNodes: (_, { attrs }) => attrs.vnodes,
  },
  props: {
    scope: String,
    isTableLoading: Boolean,
    platformOptions: Array,
    imageManagementData: Array,
    sectionsDataByDocId: Object,
    selectedSectionState: Object,
    selectedFieldState: Object,
    docsDataByDocId: Object,
    selectedExampleId: String,
  },
  emits: ['update:selectedExampleId'],
  setup(props, { emit, expose }) {
    const isDev = inject('isDev')
    const anotherScope = props.scope === 'request' ? 'response' : 'request'

    const showSearchText = ref('')
    const searchState = reactive({})

    const selectedSectionState = reactive(props.selectedSectionState || {})
    const selectedFieldState = reactive(props.selectedFieldState)

    const ensureScopeFieldState = () => {
      if (!selectedFieldState[props.scope]) {
        selectedFieldState[props.scope] = {}
      }
      return selectedFieldState[props.scope]
    }
    const numberTypeOptions = ['integer', 'float'].map(i => ({ label: i, value: i }))

    const columns = [
      {
        title: '',
        dataIndex: 'sort',
        key: 'sort',
        width: 30,
        align: 'center',
        fixed: 'left',
      },
      {
        title: 'Key',
        dataIndex: 'fieldName',
        key: 'fieldName',
        width: 300,
        fixed: 'left',
      },
      // {
      //   title: 'Used In',
      //   dataIndex: 'docs',
      //   key: 'docs',
      //   width: 200,
      // },
      {
        title: 'Required',
        dataIndex: 'required',
        key: 'required',
        width: 90,
        align: 'center',
      },
      {
        title: 'Type',
        dataIndex: 'fieldType',
        key: 'fieldType',
        width: 120,
      },
      {
        title: 'Format',
        dataIndex: 'format',
        key: 'format',
        width: 120,
      },
      {
        title: 'Platform',
        dataIndex: 'platforms',
        key: 'platforms',
        width: 200,
      },
      {
        title: 'Description',
        dataIndex: 'content',
        key: 'content',
        width: 550,
      },
      {
        title: 'Example',
        dataIndex: 'examples',
        key: 'examples',
        width: 250,
        customCell: record => ({
          'data-example-cell': record.fieldId,
        }),
      },
      {
        title: 'Action',
        dataIndex: 'action',
        key: 'action',
        width: 50,
        align: 'center',
      },
    ]

    const fieldState = computed(() => selectedFieldState[props.scope] || {})

    const exampleOptions = computed(() => Object.values(selectedSectionState.examples || {}).map(({ exampleId, exampleName }) => ({
      label: exampleName,
      value: exampleId,
    })))

    const exampleSelectDropdownStyle = {
      width: 'min(400px, calc(100vw - 32px))',
      maxWidth: 'min(400px, calc(100vw - 32px))',
    }

    const getExampleSelectPopupContainer = () => document.body

    const getSuccessExampleId = () => Object.values(selectedSectionState.examples || {}).find(({ exampleName }) => exampleName && exampleName.includes('success'))?.exampleId || Object.keys(selectedSectionState.examples || {})[0]
    const internalSelectedExampleId = ref(getSuccessExampleId())
    // emits 宣告後 onUpdate 不會出現在 useAttrs，改以 vnode.props 判斷父層是否有綁定 v-model
    const isSelectedExampleIdControlled = Object.prototype.hasOwnProperty.call(
      getCurrentInstance()?.vnode?.props ?? {},
      'selectedExampleId',
    )
    const selectedExampleId = computed({
      get: () => (isSelectedExampleIdControlled ? props.selectedExampleId : internalSelectedExampleId.value),
      set: (val) => {
        if (isSelectedExampleIdControlled) emit('update:selectedExampleId', val)
        else internalSelectedExampleId.value = val
      },
    })
    const exampleSelectOpen = ref(false)
    const editingExampleId = ref()
    const exampleInputRef = ref()

    // 新增一個 example > 選取該 example options > Save > 捨棄草稿 > 需重新判斷 selectedExampleId
    watch(() => exampleOptions.value, (newExampleOptions) => {
      console.log('[FieldTable] watch exampleOptions', newExampleOptions)
      if (isSelectedExampleIdControlled) return
      if (!newExampleOptions.map(i => i.value).includes(selectedExampleId.value)) selectedExampleId.value = getSuccessExampleId()
    })

    const expandedRowKeys = ref([])
    const knownExpandableKeys = ref(new Set())

    const collectExpandableRowKeys = (rows) => {
      const keys = []
      const walk = (list) => {
        list.forEach((row) => {
          if (row.children?.length) {
            keys.push(row.key)
            walk(row.children.filter(c => c.fieldId && !c.isNew))
          }
        })
      }
      walk(rows)
      return keys
    }

    watch(
      () => `${selectedSectionState.sectionId}#${props.scope}`,
      () => {
        expandedRowKeys.value = []
        knownExpandableKeys.value.clear()
      },
    )

    const customizedVersionSelectedDocIdByFieldId = reactive({})
    /** fieldName 輸入中暫存，blur / Enter 才寫入 fieldState */
    const fieldNameValueByFieldId = reactive({})

    const getCvSelectionKey = ({ fieldId, sectionIdFieldId }) => (
      sectionIdFieldId || `${selectedSectionState.sectionId}#${props.scope}#${fieldId}`
    )

    const getEffectiveSelectedDocId = (record) => {
      const cvKey = getCvSelectionKey(record)
      const selectedDocId = customizedVersionSelectedDocIdByFieldId[cvKey]
      if (!selectedDocId) return undefined
      const { fieldId } = record
      if (!fieldState.value[fieldId]?.customizedVersion?.[selectedDocId]) {
        delete customizedVersionSelectedDocIdByFieldId[cvKey]
        return undefined
      }
      return selectedDocId
    }

    const setSelectedCvDocId = (record, val) => {
      const cvKey = getCvSelectionKey(record)
      if (val === 'general' || !val) {
        delete customizedVersionSelectedDocIdByFieldId[cvKey]
      } else {
        customizedVersionSelectedDocIdByFieldId[cvKey] = val
      }
    }

    const getFieldStateData = (record) => {
      const { fieldId } = record
      const selectedDocId = getEffectiveSelectedDocId(record)
      return fieldState.value[fieldId]?.customizedVersion?.[selectedDocId] || fieldState.value[fieldId] || {}
    }

    const getFieldStateKey = (fieldId, key) => {
      const cvKey = `${selectedSectionState.sectionId}#${props.scope}#${fieldId}`
      const selectedDocId = customizedVersionSelectedDocIdByFieldId[cvKey]
      if (fieldState.value[fieldId]?.customizedVersion?.[selectedDocId]?.[key] !== undefined) {
        return fieldState.value[fieldId].customizedVersion[selectedDocId][key]
      }
      return fieldState.value[fieldId]?.[key] || null
    }

    const isRootParentFieldId = parentFieldId => (
      parentFieldId === null || parentFieldId === undefined || parentFieldId === '' || parentFieldId === 'ROOT'
    )

    const findParentFieldData = (record) => {
      const { parentFieldId } = record
      if (isRootParentFieldId(parentFieldId)) return {}
      return getFieldStateData({ fieldId: parentFieldId })
    }

    const getParentArrayLength = (record, exampleId = selectedExampleId.value) => {
      const parent = findParentFieldData(record)
      if (parent?.fieldType !== 'array of objects') return undefined
      return parent.examples?.[exampleId]?.arrayLength
    }

    const isParentArrayMultiItem = (record, exampleId = selectedExampleId.value) => {
      const arrayLength = getParentArrayLength(record, exampleId)
      return arrayLength != null && arrayLength >= 2
    }

    /** 沿 parentFieldId 向上找到最靠近 root 的祖先欄位 */
    const findTopParentFieldData = (record) => {
      const { parentFieldId } = record
      if (isRootParentFieldId(parentFieldId)) return {}

      let top = getFieldStateData({ fieldId: parentFieldId })
      if (!top?.fieldId) return {}

      let current = top
      while (!isRootParentFieldId(current.parentFieldId)) {
        const parent = getFieldStateData({ fieldId: current.parentFieldId })
        if (!parent?.fieldId) break
        top = parent
        current = parent
      }
      return top
    }

    const getFeldTypeOptions = (record) => {
      const { level } = record
      const options = [
        'string',
        'boolean',
        'number',
        'array of strings',
        'array of numbers',
        'array of objects',
        'stringified json',
        'object',
      ]

      if (level) {
        const topParentFieldData = getFieldStateData({ fieldId: record.parentFieldId })
        if (topParentFieldData.fieldType === 'stringified json') {
          options.push('json')
        }
      }

      return options.map(i => ({ label: i, value: i }))
    }

    const CONTAINER_FIELD_TYPES = ['stringified json', 'array of objects', 'object']
    const fieldTypeNeedsChildren = fieldType => CONTAINER_FIELD_TYPES.includes(fieldType)

    const platformsMap = computed(() => Object.fromEntries(props.platformOptions.map(p => [p.value, p.label])))

    const setDataSourceWithDefaultRow = () => {
      const docId = selectedSectionState.docId || ''
      const isMasterDoc = docId.includes('master')

      // 這裡先根據 parentFieldId / sortOrder 組成 tree，並在 docId 非 master 時套用 customizedVersion
      const effectiveByFieldId = Object.values(fieldState.value).reduce((acc, baseField) => {
        const effective = cloneDeep(baseField)
        const customized = effective.customizedVersion?.[docId]
        if (!isMasterDoc && customized) Object.assign(effective, cloneDeep(customized))

        acc[effective.fieldId] = effective
        return acc
      }, {})

      const childrenByParentId = Object.values(effectiveByFieldId).reduce((acc, field) => {
        if (field.deleted) return acc
        const parentId = field.parentFieldId || 'ROOT'
        if (!acc[parentId]) acc[parentId] = []
        acc[parentId].push(field)
        return acc
      }, {})

      console.log('childrenByParentId', childrenByParentId)

      Object.values(childrenByParentId).forEach((arr) => {
        arr.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
      })

      const rootFieldIds = (childrenByParentId.ROOT || []).map(f => f.fieldId)

      const buildNode = (fieldId) => {
        const nodeData = effectiveByFieldId[fieldId]
        if (!nodeData) return null

        const children = childrenByParentId[fieldId] || []
        const fieldsOrder = children.map(c => c.fieldId)
        const fields = fieldsOrder.reduce((acc, childId) => {
          const childNode = buildNode(childId)
          if (childNode) acc[childId] = childNode
          return acc
        }, {})

        return {
          ...nodeData,
          fieldsOrder,
          fields,
        }
      }

      const fieldsTree = rootFieldIds.reduce((acc, fieldId) => {
        const node = buildNode(fieldId)
        if (node) acc[fieldId] = node
        return acc
      }, {})

      const createDefaultNewFieldRow = ({
        parentFieldId, level, idx, sortOrder,
      }) => {
        const newFieldId = new ShortUniqueId({ length: 10 }).rnd()
        const sectionIdFieldId = `${selectedSectionState.sectionId}#${props.scope}#${newFieldId}`
        return {
          wallet: selectedSectionState.wallet,
          docId: selectedSectionState.docId,
          sectionId: selectedSectionState.sectionId,
          scope: props.scope,
          fieldId: newFieldId,
          parentFieldId,
          sectionIdFieldId,
          key: sectionIdFieldId,
          idx,
          level,
          isNew: true,
          sortOrder,
        }
      }

      const createDefaultNewChildRow = (parentFieldId, level, idxPrefix, sortOrder) => createDefaultNewFieldRow({
        parentFieldId,
        level: level + 1,
        idx: `${idxPrefix}-${sortOrder}`,
        sortOrder,
      })

      function recurseFields(fieldId, currentFields, level, idxPrefix) {
        const { fields, fieldsOrder, ...fieldData } = currentFields[fieldId]
        if (!props.platformOptions?.length
          || fieldData.deleted
          || (!isMasterDoc && fieldData.platforms && fieldData.platforms.every(platform => !platformsMap.value[platform]))) {
          return null
        }

        const row = {
          ...fieldData,
          level,
          idx: idxPrefix,
          key: fieldData.sectionIdFieldId,
        }

        if (fieldsOrder.length) {
          row.children = fieldsOrder.reduce((acc, childId, idx) => {
            const childRow = recurseFields(childId, currentFields[fieldId].fields, level + 1, `${idxPrefix}-${idx}`)
            if (childRow) acc.push(childRow)
            return acc
          }, [])

          row.children.push(createDefaultNewChildRow(fieldId, level, idxPrefix, fieldsOrder.length))
        } else if (fieldTypeNeedsChildren(fieldData.fieldType)) {
          // object / array of objects / stringified json 且尚無子欄：仍顯示一列 isNew 供編輯
          row.children = [createDefaultNewChildRow(fieldId, level, idxPrefix, 0)]
        }

        return row
      }

      const result = rootFieldIds.reduce((acc, fieldId, idx) => {
        const row = recurseFields(fieldId, cloneDeep(fieldsTree), 0, `${idx}`)
        if (row) acc.push(row)
        return acc
      }, [])

      result.push(createDefaultNewFieldRow({
        parentFieldId: 'ROOT',
        level: 0,
        idx: `${rootFieldIds.length}`,
        sortOrder: rootFieldIds.length,
      }))
      console.log('setDataSourceWithDefaultRow', result)
      return result
    }

    const dataSource = computed(() => setDataSourceWithDefaultRow())

    watch(
      dataSource,
      (rows) => {
        const keys = collectExpandableRowKeys(rows)
        const keySet = new Set(keys)

        expandedRowKeys.value = expandedRowKeys.value.filter(k => keySet.has(k))

        keys.forEach((k) => {
          if (!knownExpandableKeys.value.has(k)) {
            knownExpandableKeys.value.add(k)
            if (!expandedRowKeys.value.includes(k)) {
              expandedRowKeys.value = [...expandedRowKeys.value, k]
            }
          }
        })

        knownExpandableKeys.value.forEach((k) => {
          if (!keySet.has(k)) knownExpandableKeys.value.delete(k)
        })
      },
      { immediate: true, deep: true },
    )

    watch(
      [
        // () => props.rerenderChildTable,
        () => props.selectedSectionState.docId,
        () => props.selectedSectionState.wallet,
        () => props.selectedSectionState.sectionId,
        // () => props.platformOptions,
      ],
      async ([newDocId, newWallet, newSectionId], [oldDocId, oldWallet, oldSectionId]) => {
        console.log(props.scope, '[FieldTable] watch selectedSectionState.docId & wallet & sectionId', {
          newDocId, oldDocId, newWallet, oldWallet, newSectionId, oldSectionId,
        })

        if (props.selectedSectionState.sectionType !== 'api'
          || (newWallet === oldWallet && newDocId === oldDocId && newSectionId === oldSectionId)
          || !newDocId || !newWallet || !newSectionId) return

        if (!isSelectedExampleIdControlled) selectedExampleId.value = getSuccessExampleId()
        emptyObject(fieldNameValueByFieldId)

        // const { sectionId, wallet } = selectedSectionState
        // isTableLoading.value = true
        // await props.fetchFieldsData()

        const clonedSelectedSectionState = cloneDeep(props.selectedSectionState) // 一定要先 clone 避免被 emptyObject 清空
        emptyObject(selectedSectionState)
        Object.assign(selectedSectionState, clonedSelectedSectionState)

        // emptyObject(selectedFieldState)
        // Object.assign(selectedFieldState, props.selectedFieldState)

        // // 草稿資料覆蓋
        // if (props.draftSection.updatedDataBySectionId?.[sectionId]?.fieldsData) {
        //   props.draftSection.updatedDataBySectionId[sectionId].fieldsData.forEach((field) => {
        //     const { fieldId, scope } = field
        //     fieldsByWallet[wallet][sectionId][scope][fieldId] = field
        //   })
        // }

        // console.log('fieldsByWallet', fieldsByWallet)
        // isTableLoading.value = false

        // console.log(props.scope, '[FieldTable] watch dataSource', dataSource.value)
        // // Request 會先接收到此 watch，須等到 Response 也接收到 watch 後，再 setRerenderChildTable(false)，否則 Response 會沒接收到而無法觸發更新
        // if (newRerenderChildTable && props.scope === 'response') props.setRerenderChildTable(false)
      },
      { deep: true },
    )

    const getRawExampleForRecord = (record, exampleId = selectedExampleId.value) => (
      getFieldStateData(record).examples?.[exampleId]
      ?? record.examples?.[exampleId]
    )

    const getExampleScalarForRecord = (record) => {
      const raw = getRawExampleForRecord(record)
      if (Array.isArray(raw) && !isParentArrayMultiItem(record)) {
        return normalizeExampleValueForInput(raw[0] ?? '')
      }
      return normalizeExampleValueForInput(raw)
    }

    /** 子欄位若位於 array of objects 下且父層 arrayLength >= 2，回傳 example 陣列供多個 input 顯示 */
    const getExampleListForRecord = (record) => {
      const exampleId = selectedExampleId.value
      const raw = getRawExampleForRecord(record, exampleId)
      const parent = findParentFieldData(record)
      if (parent?.fieldType === 'array of objects') {
        const arrayLength = getParentArrayLength(record, exampleId)
        if (arrayLength == null || arrayLength < 2) return null
        const arr = Array.isArray(raw)
          ? raw.map(normalizeExampleValueForInput)
          : Array.from({ length: arrayLength }, (_, i) => (i === 0 ? normalizeExampleValueForInput(raw) : ''))
        while (arr.length < arrayLength) arr.push('')
        return arr.slice(0, arrayLength)
      }
      return Array.isArray(raw) ? raw.map(normalizeExampleValueForInput) : null
    }

    const syncChildExampleArray = (parentRecord, childFieldId, childRowIdx, selectedExampleIdVal, mutate) => {
      const childState = fieldState.value[childFieldId]
      const rowChild = parentRecord.children[childRowIdx]
      const prev = childState?.examples?.[selectedExampleIdVal]
        ?? rowChild?.examples?.[selectedExampleIdVal]
        ?? ''
      const next = mutate(prev)

      if (childState) {
        if (!childState.examples) childState.examples = {}
        childState.examples[selectedExampleIdVal] = next
      }
      if (rowChild) {
        if (!rowChild.examples) rowChild.examples = {}
        rowChild.examples[selectedExampleIdVal] = next
      }

      const parentState = fieldState.value[parentRecord.fieldId]
      if (parentState?.fields?.[childFieldId]) {
        if (!parentState.fields[childFieldId].examples) parentState.fields[childFieldId].examples = {}
        parentState.fields[childFieldId].examples[selectedExampleIdVal] = next
      }
    }

    const findDataSouceLevel = (record) => {
      console.log('findDataSouceLevel', record.fieldName, `(${record.fieldId})`, record)
      const { idx } = record

      // let currentLevelParentField = null
      // if (parentFieldId !== 'ROOT') {
      //   currentLevelParentField = fieldState.value[parentFieldId]
      // }

      // console.log(fieldId, { currentLevelParentField, currentLevelFields, currentLevelFieldsOrder })

      const idxArr = idx.split('-') // [0,2,1]
      let currentLevelDataSource = dataSource.value
      let currentLevelDataSourceObj = currentLevelDataSource[idxArr[0]]

      idxArr.slice(1).forEach((i, index) => { // [2,1]
        currentLevelDataSource = currentLevelDataSource[idxArr[index]].children // dataSource.value[0].children[2].children
        currentLevelDataSourceObj = currentLevelDataSourceObj.children[i]
      })
      console.log({ idxArr, currentLevelDataSource, currentLevelDataSourceObj })

      return {
        // currentLevelParentField,
        // currentLevelFields,
        // currentLevelFieldsOrder,
        currentLevelDataSource,
        currentLevelDataSourceObj,
      }
    }

    // eslint-disable-next-line no-unused-vars
    const findDataSourceLevel = (record) => {
      console.log('findDataSourceLevel', record.fieldName, `(${record.fieldId})`, record)
      const { idx } = record

      const idxArr = idx.split('-') // [0,2,1]
      let currentLevelDataSource = dataSource.value
      let currentLevelDataSourceObj = currentLevelDataSource[idxArr[0]]

      idxArr.slice(1).forEach((i, index) => { // [2,1]
        currentLevelDataSource = currentLevelDataSource[idxArr[index]].children // dataSource.value[0].children[2].children
        currentLevelDataSourceObj = currentLevelDataSourceObj.children[i]
      })
      console.log({ idxArr, currentLevelDataSource, currentLevelDataSourceObj })

      return {
        currentLevelDataSource,
        currentLevelDataSourceObj,
      }
    }

    const onChange = (val, key, record) => {
      ensureScopeFieldState()

      const { fieldId } = record
      const selectedDocId = getEffectiveSelectedDocId(record)

      if (!fieldState.value[fieldId]) {
        const newFieldData = cloneDeep(record)
        if (fieldNameValueByFieldId[fieldId]) {
          newFieldData.fieldName = fieldNameValueByFieldId[fieldId].trim()
        }
        newFieldData.added = true
        delete newFieldData.key
        delete newFieldData.idx
        delete newFieldData.isNew
        delete newFieldData.children

        if (selectedDocId && selectedDocId !== 'general') {
          fieldState.value[fieldId] = { customizedVersion: { [selectedDocId]: newFieldData } }
        } else {
          fieldState.value[fieldId] = newFieldData
        }
      }

      const fieldData = selectedDocId && selectedDocId !== 'general'
        ? fieldState.value[fieldId].customizedVersion[selectedDocId]
        : fieldState.value[fieldId]

      console.log('[FieldTable] onChange', {
        fieldData, key, val, record,
      })

      if (key !== 'fieldName' && val == null) {
        delete fieldData[key]
      } else {
        fieldData[key] = val
        record[key] = val
      }

      if (key === 'fieldType') {
        // 子列由 setDataSourceWithDefaultRow 依 fieldType 產生（dataSource 為 computed，勿在此 mutate record.children）

        if (val !== 'string') {
          delete fieldData.maxLength
          delete record.maxLength
        }
        if (val !== 'number') {
          delete fieldData.numberType
          delete fieldData.decimalPlace
          delete record.numberType
          delete record.decimalPlace
        }
        if (['array of objects', 'object'].includes(val)) {
          Object.keys(fieldData.examples).forEach((exampleId) => {
            if (typeof fieldData.examples[exampleId] === 'string') delete fieldData.examples[exampleId]
          })
        }
      }

      // 寫入 fieldState 後 computed 會重建列；先清掉當前列 isNew，避免同一輪 render 內拖曳等仍被 v-if="!record.isNew" 擋住
      if (record.isNew) {
        delete record.isNew
      }
    }

    const getCommittedFieldName = fieldId => getFieldStateKey(fieldId, 'fieldName') ?? ''

    const getFieldNameInputValue = (record) => {
      const { fieldId } = record
      if (fieldNameValueByFieldId[fieldId]) return fieldNameValueByFieldId[fieldId]
      return getCommittedFieldName(fieldId)
    }

    const onChangeFieldNameValue = (val, record) => {
      fieldNameValueByFieldId[record.fieldId] = val.trim()
    }

    const onBlurFieldNameInput = (record) => {
      const { fieldId } = record
      if (!fieldNameValueByFieldId[fieldId]) return

      const nextVal = fieldNameValueByFieldId[fieldId].trim()
      delete fieldNameValueByFieldId[fieldId]

      if (nextVal === getCommittedFieldName(fieldId)) return
      onChange(nextVal, 'fieldName', record)
    }

    const fieldTableRootRef = ref(null)
    const fieldTableRef = ref(null)
    const pendingExampleFormatErrorFocusKey = ref(null)
    const exampleFormatFocusGeneration = ref(0)
    const exampleFormatInputRegistry = reactive({})

    provide('pendingExampleFormatFocusKey', pendingExampleFormatErrorFocusKey)
    provide('exampleFormatFocusGeneration', exampleFormatFocusGeneration)
    provide('exampleFormatInputRegistry', exampleFormatInputRegistry)

    const requestExampleFormatInputFocus = (key) => {
      pendingExampleFormatErrorFocusKey.value = key
      exampleFormatFocusGeneration.value += 1
    }

    const isVisibleFocusableInput = (input) => {
      if (!input || input.disabled || input.readOnly) return false
      const rect = input.getBoundingClientRect()
      if (!rect.width || !rect.height) return false
      const style = window.getComputedStyle(input)
      return style.visibility !== 'hidden' && style.display !== 'none'
    }

    const findFirstExampleFormatError = () => {
      const exampleIds = exampleOptions.value.map(({ value }) => value)
      return findExampleFormatErrorInScopeFields(fieldState.value, exampleIds)
    }

    const checkExampleFormatError = () => !!findFirstExampleFormatError()

    const getFieldTableElement = () => {
      const tableInst = fieldTableRef.value
      if (tableInst?.$el) return tableInst.$el
      const rowInst = fieldTableRootRef.value
      const rowEl = rowInst && (rowInst.$el ?? rowInst)
      return rowEl?.querySelector?.('.field-table') || rowEl
    }

    const getFieldTableScopeRoot = () => (
      document.querySelector(`[data-field-table-scope="${props.scope}"]`)
      || getFieldTableElement()
    )

    const getExampleFormatErrorInput = (keyOrKeys, root = getFieldTableScopeRoot()) => {
      const keys = Array.isArray(keyOrKeys) ? keyOrKeys : [keyOrKeys]
      const scopeRoot = root || getFieldTableScopeRoot()
      const searchRoots = []
      if (scopeRoot) {
        const tableBody = scopeRoot.querySelector('.ant-table-body')
        if (tableBody) searchRoots.push(tableBody)
        searchRoots.push(scopeRoot)
      } else {
        const fallback = document.querySelector(`[data-field-table-scope="${props.scope}"]`)
        if (fallback) searchRoots.push(fallback)
      }

      for (const key of keys) {
        for (const searchRoot of searchRoots) {
          const holders = searchRoot.querySelectorAll(`[data-json-example-input="${key}"]`)
          for (const holder of holders) {
            const input = holder.matches('input,textarea') ? holder : holder.querySelector('input,textarea')
            if (isVisibleFocusableInput(input)) return input
          }
        }
      }
      return null
    }

    const getExampleFormatInputKeys = (errOrKey) => {
      if (typeof errOrKey === 'string') return [errOrKey]
      return getExampleFormatInputKeyCandidates(errOrKey)
    }

    const resolveExampleFormatInputKey = (err) => {
      const keys = getExampleFormatInputKeys(err)
      for (const key of keys) {
        if (exampleFormatInputRegistry[key]) return key
      }
      for (const key of keys) {
        if (getExampleFormatErrorInput(key)) return key
      }
      return buildExampleFormatInputKey(err)
    }

    const SCROLL_PADDING = 48

    const getFormatErrorScrollTarget = element => (
      element?.closest('.example-item-body')
      || element?.closest('td')
      || element?.closest('tr.ant-table-row')
      || element
    )

    const isScopeTabVisible = () => {
      const root = getFieldTableScopeRoot()
      if (!root) return false
      const pane = root.closest('.ant-tabs-tabpane')
      if (pane && !pane.classList.contains('ant-tabs-tabpane-active')) return false
      const rect = root.getBoundingClientRect()
      return root.offsetParent !== null && rect.width > 0 && rect.height > 0
    }

    const waitForScopeTabVisible = async (maxMs = 4000) => {
      const deadline = Date.now() + maxMs
      while (Date.now() < deadline) {
        if (isScopeTabVisible()) return true
        await nextTick()
        await new Promise(resolve => requestAnimationFrame(resolve))
      }
      return isScopeTabVisible()
    }

    const getExampleColumnFallbackScrollLeft = (padding = SCROLL_PADDING) => {
      let sum = 0
      for (const col of columns) {
        if (col.key === 'examples') break
        if (!col.fixed) sum += col.width || 0
      }
      return Math.max(0, sum - padding)
    }

    const getExampleCellInRow = (rowOrFieldId) => {
      let row = rowOrFieldId
      if (typeof rowOrFieldId === 'string') {
        const root = getFieldTableScopeRoot()
        row = root?.querySelector(`tr[data-field-id="${rowOrFieldId}"]`)
      }
      if (!row) return null
      return row.querySelector('[data-json-example-input]')?.closest('td')
        || row.querySelector(`td[data-example-cell="${row.dataset.fieldId}"]`)
        || row.querySelector('.example-item-body')?.closest('td')
        || row.cells?.[8]
    }

    const syncTableHeaderScroll = (tableRoot) => {
      const tableBody = tableRoot?.querySelector('.ant-table-body')
      const tableHeader = tableRoot?.querySelector('.ant-table-header')
      if (tableBody && tableHeader) tableHeader.scrollLeft = tableBody.scrollLeft
    }

    const applyTableBodyScrollLeft = (tableRoot, scrollLeft) => {
      const tableBody = tableRoot?.querySelector('.ant-table-body')
      if (!tableBody) return false
      const maxScroll = Math.max(0, tableBody.scrollWidth - tableBody.clientWidth)
      const next = Math.max(0, Math.min(scrollLeft, maxScroll))
      if (tableBody.scrollLeft !== next) {
        tableBody.scrollLeft = next
        tableBody.dispatchEvent(new Event('scroll', { bubbles: true }))
      }
      syncTableHeaderScroll(tableRoot)
      return true
    }

    const getTableHorizontalScrollBounds = (tableRoot) => {
      const tableBody = tableRoot?.querySelector('.ant-table-body')
      if (!tableBody) return null
      const bodyRect = tableBody.getBoundingClientRect()
      const fixedLeftEl = tableRoot.querySelector('.ant-table-cell-fix-left-last')
      const fixedRightEl = tableRoot.querySelector('.ant-table-cell-fix-right-first')
      return {
        tableBody,
        scrollAreaLeft: fixedLeftEl ? fixedLeftEl.getBoundingClientRect().right : bodyRect.left,
        scrollAreaRight: fixedRightEl ? fixedRightEl.getBoundingClientRect().left : bodyRect.right,
      }
    }

    const waitForTableLayoutReady = async (maxMs = 4000) => {
      const deadline = Date.now() + maxMs
      while (Date.now() < deadline) {
        await waitForScopeTabVisible(200)
        const tableRoot = getFieldTableElement()
        const tableBody = tableRoot?.querySelector('.ant-table-body')
        if (tableBody?.clientWidth > 0 && tableBody.scrollWidth > 0) return tableRoot
        await nextTick()
        await new Promise(resolve => requestAnimationFrame(resolve))
        await new Promise(resolve => setTimeout(resolve, 50))
      }
      return getFieldTableElement()
    }

    const scrollTableBodyHorizontally = (element, padding = SCROLL_PADDING) => {
      const tableRoot = getFieldTableElement()
      const bounds = getTableHorizontalScrollBounds(tableRoot)
      if (!bounds?.tableBody || !element) return false

      const row = element.closest?.('tr')
      const cell = getExampleCellInRow(row)
        || element.closest('td')
        || element
      const { tableBody, scrollAreaLeft } = bounds
      const bodyRect = tableBody.getBoundingClientRect()
      const cellRect = cell.getBoundingClientRect()

      let targetScrollLeft = getExampleColumnFallbackScrollLeft(padding)
      if (cellRect.width > 0 || cellRect.height > 0) {
        const fixedLeftWidth = scrollAreaLeft - bodyRect.left
        const elementLeftInScroll = cellRect.left - bodyRect.left + tableBody.scrollLeft
        targetScrollLeft = elementLeftInScroll - fixedLeftWidth - padding
      }

      return applyTableBodyScrollLeft(tableRoot, targetScrollLeft)
    }

    const getScrollableAncestors = (element) => {
      const ancestors = []
      let node = element?.parentElement
      while (node && node !== document.documentElement) {
        const { overflowX, overflowY } = window.getComputedStyle(node)
        const scrollX = ['auto', 'scroll', 'overlay'].includes(overflowX) && node.scrollWidth > node.clientWidth + 1
        const scrollY = ['auto', 'scroll', 'overlay'].includes(overflowY) && node.scrollHeight > node.clientHeight + 1
        if (scrollX || scrollY) ancestors.push({ node, scrollX, scrollY })
        node = node.parentElement
      }
      return ancestors
    }

    const revealElementVertically = (element, padding = SCROLL_PADDING) => {
      if (!element) return
      getScrollableAncestors(element).forEach(({ node, scrollY }) => {
        if (!scrollY) return
        const elRect = element.getBoundingClientRect()
        const nodeRect = node.getBoundingClientRect()
        if (!elRect.width && !elRect.height) return

        if (elRect.top < nodeRect.top + padding) {
          node.scrollTop += elRect.top - nodeRect.top - padding
        } else if (elRect.bottom > nodeRect.bottom - padding) {
          node.scrollTop += elRect.bottom - nodeRect.bottom + padding
        }
      })
    }

    const revealExampleFormatError = async (element) => {
      const target = getFormatErrorScrollTarget(element)
      const exampleCell = getExampleCellInRow(target?.closest?.('tr')) || target
      for (let i = 0; i < 6; i += 1) {
        scrollTableBodyHorizontally(exampleCell)
        revealElementVertically(target)
        scrollTableBodyHorizontally(exampleCell)
        await new Promise(resolve => requestAnimationFrame(resolve))
      }
    }

    const scrollToFieldRow = async (fieldId) => {
      await waitForScopeTabVisible()
      await waitForTableLayoutReady()
      const root = getFieldTableScopeRoot()
      const row = root?.querySelector(`tr[data-field-id="${fieldId}"]`)
      if (!row) return
      const exampleCell = getExampleCellInRow(row)
      await revealExampleFormatError(exampleCell || row)
    }

    const isExampleFormatErrorInputReady = input => isVisibleFocusableInput(input)

    const resolveFocusableInput = (input) => {
      if (!input) return null
      if (input.matches?.('input,textarea')) return input
      return input.querySelector?.('input,textarea')
    }

    const focusNativeInputElement = (input) => {
      const target = resolveFocusableInput(input)
      if (!isVisibleFocusableInput(target)) return false

      const { activeElement } = document
      if (activeElement && activeElement !== target && activeElement.blur) activeElement.blur()

      target.focus({ preventScroll: true })
      if (document.activeElement !== target) {
        target.dispatchEvent(new MouseEvent('mousedown', { bubbles: true, cancelable: true, view: window }))
        target.dispatchEvent(new MouseEvent('mouseup', { bubbles: true, cancelable: true, view: window }))
        target.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }))
        target.focus({ preventScroll: true })
      }

      if (document.activeElement === target) {
        const len = target.value?.length ?? 0
        if (target.setSelectionRange) target.setSelectionRange(len, len)
        return true
      }
      return false
    }

    const focusExampleInputByKey = (keyOrErr) => {
      exampleSelectOpen.value = false
      editingExampleId.value = null
      const keys = getExampleFormatInputKeys(keyOrErr)
      const key = typeof keyOrErr === 'string' ? keyOrErr : resolveExampleFormatInputKey(keyOrErr)
      requestExampleFormatInputFocus(key)

      for (const candidateKey of keys) {
        const registryEntry = exampleFormatInputRegistry[candidateKey]
        if (registryEntry?.focus?.()) return true
      }
      return focusNativeInputElement(getExampleFormatErrorInput(keys))
    }

    const waitForExampleFormatErrorInputByErr = async (err, maxMs = 4000) => {
      const keys = getExampleFormatInputKeys(err)
      const deadline = Date.now() + maxMs
      while (Date.now() < deadline) {
        const input = getExampleFormatErrorInput(keys)
        if (isExampleFormatErrorInputReady(input)) return input
        await nextTick()
        await new Promise(resolve => requestAnimationFrame(resolve))
      }
      return null
    }

    const tryFocusExampleFormatErrorInput = async (err) => {
      const keys = getExampleFormatInputKeys(err)
      for (let attempt = 0; attempt < 12; attempt += 1) {
        await waitForScopeTabVisible(300)
        const currentInput = getExampleFormatErrorInput(keys)
        if (currentInput) await revealExampleFormatError(currentInput)
        await nextTick()
        for (const candidateKey of keys) {
          const registryEntry = exampleFormatInputRegistry[candidateKey]
          if (registryEntry?.focus?.()) return true
        }
        if (focusNativeInputElement(getExampleFormatErrorInput(keys))) return true
        await new Promise(resolve => setTimeout(resolve, 80))
      }
      return focusExampleInputByKey(err)
    }

    const focusExampleFormatErrorInput = async (errOverride) => {
      const err = errOverride || findFirstExampleFormatError()
      if (!err) return false

      await waitForScopeTabVisible()
      await waitForTableLayoutReady()

      if (err.exampleId !== selectedExampleId.value) {
        selectedExampleId.value = err.exampleId
        await nextTick()
        await nextTick()
        await waitForTableLayoutReady()
      }

      const key = resolveExampleFormatInputKey(err)
      requestExampleFormatInputFocus(key)
      await scrollToFieldRow(err.fieldId)

      const input = await waitForExampleFormatErrorInputByErr(err)
      if (!input) {
        console.warn('[FieldTable focus] input not found', props.scope, { err, keys: getExampleFormatInputKeys(err), registryKeys: Object.keys(exampleFormatInputRegistry) })
      }

      const errorEl = getFormatErrorScrollTarget(input)?.querySelector('.ant-form-item-explain-error')
      if (errorEl?.offsetParent) await revealExampleFormatError(errorEl)

      return tryFocusExampleFormatErrorInput(err)
    }

    const refocusExampleFormatErrorInput = async () => {
      const pendingKey = pendingExampleFormatErrorFocusKey.value
      if (!pendingKey) return false
      const err = findFirstExampleFormatError()
      if (err) return tryFocusExampleFormatErrorInput(err)
      return tryFocusExampleFormatErrorInput(pendingKey)
    }

    expose({
      checkExampleFormatError,
      focusExampleFormatErrorInput,
      refocusExampleFormatErrorInput,
    })

    const onChangeExampleField = (val, record, idx) => {
      console.log('[FieldTable] onChangeExampleField val, record', val, record)
      ensureScopeFieldState()

      const fieldData = fieldState.value[record.fieldId]
      if (!fieldData.examples) fieldData.examples = {}
      if (fieldData.examples[selectedExampleId.value] === undefined) {
        fieldData.examples[selectedExampleId.value] = idx !== undefined ? [] : ''
      }

      if (!record.examples) record.examples = {}

      if (idx === undefined) {
        fieldData.examples[selectedExampleId.value] = val
        record.examples[selectedExampleId.value] = val
      } else {
        const parent = findParentFieldData(record)
        const arrayLength = getParentArrayLength(record)
        let exampleList = fieldData.examples[selectedExampleId.value]
        if (parent?.fieldType === 'array of objects' && arrayLength != null && arrayLength >= 2 && !Array.isArray(exampleList)) {
          exampleList = Array.from({ length: arrayLength }, (_, i) => (i === 0 ? (exampleList ?? '') : ''))
          fieldData.examples[selectedExampleId.value] = exampleList
        }
        if (!Array.isArray(exampleList)) {
          exampleList = []
          fieldData.examples[selectedExampleId.value] = exampleList
        }
        exampleList[idx] = val

        let recordExampleList = record.examples[selectedExampleId.value]
        if (parent?.fieldType === 'array of objects' && arrayLength != null && arrayLength >= 2 && !Array.isArray(recordExampleList)) {
          recordExampleList = Array.from({ length: arrayLength }, (_, i) => (i === 0 ? (recordExampleList ?? '') : ''))
          record.examples[selectedExampleId.value] = recordExampleList
        }
        if (!Array.isArray(recordExampleList)) {
          recordExampleList = []
          record.examples[selectedExampleId.value] = recordExampleList
        }
        recordExampleList[idx] = val
      }
    }

    const onBlurExampleInput = (exampleName) => {
      const exampleId = editingExampleId.value
      const index = exampleOptions.value.findIndex(option => option.value === exampleId)
      if (index > 0 && exampleId && selectedSectionState.examples?.[exampleId]) {
        selectedSectionState.examples[exampleId].exampleName = exampleName
      }

      console.log('[FieldTable] onBlurExampleInput exampleName', exampleName)
      // 加上判斷式來預防當 editingExampleId = examp1e#1 時，按下 Edit examp1e#2 後 focus 不會直接聚焦到 examp1e#2，需要再按一次才能聚焦的問題
      editingExampleId.value = null
    }

    const onChangeExampleInput = (newExampleName, exampleId) => {
      console.log('[FieldTable] onChangeExampleInput newExampleName, exampleId', newExampleName, exampleId)
      selectedSectionState.examples[exampleId].exampleName = newExampleName
    }

    const onFocusExampleInput = (e) => {
      console.log('[FieldTable] onFocusExampleInput', e)
      exampleSelectOpen.value = true
    }

    const dropdownVisibleChange = (val) => {
      console.log('[FieldTable] dropdownVisibleChange val', val)
      if (val) {
        console.log(111)
        exampleSelectOpen.value = val
      } else if (!editingExampleId.value) {
        console.log(222, 'editingExampleId', editingExampleId.value, 'selectedExampleId', selectedExampleId.value)
        exampleSelectOpen.value = val
      }
    }

    const onChangeSelectedExample = (val) => {
      console.log('[FieldTable] onChangeSelectedExample', val)
      selectedExampleId.value = val
      exampleSelectOpen.value = false
      editingExampleId.value = false
      console.log('[FieldTable] onChangeSelectedExample exampleSelectOpen.value', exampleSelectOpen.value)
    }

    const onClickAddExample = () => {
      console.log('[FieldTable] onClickAddExample')
      const exampleId = new ShortUniqueId({ length: 10 }).rnd()
      const exampleName = 'New example'

      if (!selectedSectionState.examples) selectedSectionState.examples = {}
      selectedSectionState.examples[exampleId] = { exampleId, exampleName }
      editingExampleId.value = exampleId

      nextTick(() => {
        console.log('exampleInputRef.value', exampleInputRef.value)
        exampleInputRef.value.focus()
      })
    }

    const onClickEditExample = (e, exampleId) => {
      console.log('[FieldTable] c', e, exampleId)
      e.stopPropagation()
      e.preventDefault()

      editingExampleId.value = exampleId

      nextTick(() => {
        exampleInputRef.value.focus()
      })
    }

    const onClickCopyExample = (e, sourceExampleId) => {
      e.stopPropagation()
      e.preventDefault()

      const sourceExample = selectedSectionState.examples?.[sourceExampleId]
      if (!sourceExample) return

      const newExampleId = new ShortUniqueId({ length: 10 }).rnd()
      const exampleName = `Copy ${sourceExample.exampleName}`

      if (!selectedSectionState.examples) selectedSectionState.examples = {}
      selectedSectionState.examples[newExampleId] = {
        ...cloneDeep(sourceExample),
        exampleId: newExampleId,
        exampleName,
      }

      Object.values(selectedFieldState).forEach((scope) => {
        Object.values(scope).forEach((field) => {
          if (field.examples?.[sourceExampleId] === undefined) return
          if (!field.examples) field.examples = {}
          field.examples[newExampleId] = cloneDeep(field.examples[sourceExampleId])
        })
      })

      selectedExampleId.value = newExampleId
      editingExampleId.value = newExampleId
      exampleSelectOpen.value = true

      nextTick(() => {
        if (exampleInputRef.value) exampleInputRef.value.focus()
      })
    }

    const onClickDeleteExample = (e, exampleId) => {
      console.log('[FieldTable] onClickDeleteExample', e, exampleId)
      e.stopPropagation()
      if (editingExampleId.value === exampleId) editingExampleId.value = null
      if (selectedExampleId.value === exampleId) {
        selectedExampleId.value = exampleOptions.value[0]?.value
      }

      delete selectedSectionState.examples[exampleId]

      Object.values(selectedFieldState).forEach((scope) => {
        Object.values(scope).forEach((field) => {
          if (field.examples?.[exampleId]) delete field.examples[exampleId]
        })
      })
    }

    const selectedArrayItemIdx = ref(0)

    const onChangeSelectedArrayItemIdx = (val) => {
      console.log('[FieldTable] onChangeSelectedArrayItemIdx', val)
      selectedArrayItemIdx.value = val
    }

    const onClickAddArrayItem = (record) => {
      console.log('[FieldTable] onClickAddArrayItem', record)
      const fieldData = fieldState.value[record.fieldId]
      const selectedExampleIdVal = selectedExampleId.value

      if (!fieldData.examples) fieldData.examples = {}
      if (!fieldData.examples[selectedExampleIdVal]) fieldData.examples[selectedExampleIdVal] = {}
      const { arrayLength = 1 } = fieldData.examples[selectedExampleIdVal] || {}
      fieldData.examples[selectedExampleIdVal].arrayLength = arrayLength + 1
      console.log('[FieldTable] onClickAddArrayItem fieldData', fieldData)

      record.children.forEach(({ fieldId: childFieldId }, childIdx) => {
        if (!childFieldId || record.children[childIdx]?.isNew) return

        syncChildExampleArray(record, childFieldId, childIdx, selectedExampleIdVal, (prev) => {
          if (!Array.isArray(prev)) return [prev ?? '', '']
          return [...prev, '']
        })
      })
    }

    const onClickRemoveArrayItem = (record) => {
      console.log('[FieldTable] onClickRemoveArrayItem', record)
      const fieldData = fieldState.value[record.fieldId]
      const selectedExampleIdVal = selectedExampleId.value
      const newArrayLength = fieldData.examples[selectedExampleIdVal].arrayLength - 1
      fieldData.examples[selectedExampleIdVal].arrayLength = newArrayLength

      if (newArrayLength < 2) {
        delete fieldData.examples[selectedExampleIdVal].arrayLength
        if (Object.keys(fieldData.examples[selectedExampleIdVal]).length === 0) {
          delete fieldData.examples[selectedExampleIdVal]
        }
      }

      const removeIdx = selectedArrayItemIdx.value
      record.children.forEach(({ fieldId: childFieldId }, childIdx) => {
        if (!childFieldId || record.children[childIdx]?.isNew) return

        syncChildExampleArray(record, childFieldId, childIdx, selectedExampleIdVal, (prev) => {
          const arr = Array.isArray(prev) ? [...prev] : [prev ?? '']
          arr.splice(removeIdx, 1)
          if (newArrayLength < 2) return arr[0] ?? ''
          return arr
        })
      })

      selectedArrayItemIdx.value = 0
    }

    const parentFieldIdKey = (parentFieldId) => {
      if (parentFieldId === null || parentFieldId === undefined || parentFieldId === '' || parentFieldId === 'ROOT') {
        return 'ROOT'
      }
      return parentFieldId
    }

    /** 刪除後將同層未刪除欄位的 sortOrder 重編為 0..n-1（與後端 reorder 一致） */
    const compactSiblingSortOrders = (parentFieldId) => {
      const pKey = parentFieldIdKey(parentFieldId)
      const siblings = Object.values(fieldState.value)
        .filter(f => !f.deleted && parentFieldIdKey(f.parentFieldId) === pKey)
        .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))

      siblings.forEach((field, idx) => {
        field.sortOrder = idx
      })
    }

    const collectDescendantFieldIds = (parentId) => {
      const descendants = []
      const visit = (currentParentId) => {
        Object.values(fieldState.value).forEach((field) => {
          if (!field || field.deleted) return
          if (parentFieldIdKey(field.parentFieldId) !== parentFieldIdKey(currentParentId)) return
          descendants.push(field.fieldId)
          visit(field.fieldId)
        })
      }
      visit(parentId)
      return descendants
    }

    const deleteFieldEntry = (targetFieldId) => {
      const field = fieldState.value[targetFieldId]
      if (!field) return
      if (field.added) {
        delete fieldState.value[targetFieldId]
      } else {
        field.deleted = true
      }
    }

    const onDeleteField = async (record) => {
      const { fieldId, parentFieldId } = record
      collectDescendantFieldIds(fieldId).forEach(deleteFieldEntry)
      deleteFieldEntry(fieldId)
      compactSiblingSortOrders(parentFieldId)
    }

    const getDescendantFieldDisplayNames = parentFieldId => (
      collectDescendantFieldIds(parentFieldId).map((id) => {
        const name = getCommittedFieldName(id)
        return name || id
      })
    )

    const shouldConfirmFieldTypeChange = (record, nextFieldType) => {
      const currentFieldType = getFieldStateKey(record.fieldId, 'fieldType')
      if (!fieldTypeNeedsChildren(currentFieldType)) return false
      if (fieldTypeNeedsChildren(nextFieldType)) return false
      return collectDescendantFieldIds(record.fieldId).length > 0
    }

    const renderFieldTypeChangeConfirmContent = (currentFieldType, nextFieldType, parentFieldName, childNames) => h('div', [
      h('div', `${parentFieldName} 欄位類型從 ${currentFieldType} 變更為 ${nextFieldType}，將會移除其子欄位：`),
      h(
        'div',
        {
          style: {
            margin: '8px 0', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '6px',
          },
        },
        childNames.map(name => h(AntTag, { key: name, color: 'blue' }, () => name)),
      ),
      h('div', '確認要變更嗎？'),
    ])

    const onChangeFieldType = (val, record) => {
      if (!shouldConfirmFieldTypeChange(record, val)) {
        onChange(val, 'fieldType', record)
        return
      }

      const currentFieldType = getFieldStateKey(record.fieldId, 'fieldType')
      const parentFieldName = getCommittedFieldName(record.fieldId) || record.fieldId
      const childNames = getDescendantFieldDisplayNames(record.fieldId)

      AntModal.confirm({
        title: '確認變更欄位類型',
        content: renderFieldTypeChangeConfirmContent(currentFieldType, val, parentFieldName, childNames),
        okText: '確認',
        cancelText: '取消',
        onOk: () => {
          collectDescendantFieldIds(record.fieldId).forEach(deleteFieldEntry)
          onChange(val, 'fieldType', record)
        },
      })
    }

    const onMouseEnterDrag = (e) => {
      const rowParent = e.target.closest('.ant-table-row')
      if (rowParent) rowParent.draggable = true
    }

    const onMouseLeaveDrag = (e) => {
      const rowParent = e.target.closest('.ant-table-row')
      if (rowParent) rowParent.draggable = false
    }

    const dragObj = ref({})
    const dropObj = ref({})
    const customRow = (record) => {
      const onDragstart = (e) => {
        e.stopPropagation()
        dragObj.value = record
        e.dataTransfer.effectAllowed = 'move'
        console.log('onDragstart', record.idx)
      }
      const onDragover = (e) => {
        e.preventDefault()
        e.dataTransfer.dropEffect = 'move'
        if (record.isNew || dragObj.value.isNew) return
        updateTableRowDragIndicator(e.currentTarget, e)
      }
      const onDragenter = (e) => {
        e.preventDefault()
        if (record.isNew || dragObj.value.isNew) return
        updateTableRowDragIndicator(e.currentTarget, e)
      }
      const onDragleave = (e) => {
        const row = e.currentTarget
        if (!row.contains(e.relatedTarget)) clearTableRowDragIndicator(row)
      }
      const onDragend = (e) => {
        clearTableRowDragIndicator(e.currentTarget)
        e.currentTarget.draggable = false
      }
      const onDrop = (e) => {
        e.stopPropagation()
        console.log('onDrop', { e, dropObj: record, dragObj: dragObj.value })
        dropObj.value = record
        const row = e.currentTarget
        clearTableRowDragIndicator(row)

        if (record.isNew || dragObj.value.isNew) return
        if (parentFieldIdKey(record.parentFieldId) !== parentFieldIdKey(dragObj.value.parentFieldId)) return

        const { currentLevelDataSource } = findDataSouceLevel(record)
        const rows = currentLevelDataSource.filter(r => !r.isNew)
        const fromIndex = rows.findIndex(r => r.fieldId === dragObj.value.fieldId)
        const targetIndex = rows.findIndex(r => r.fieldId === record.fieldId)
        if (fromIndex === -1 || targetIndex === -1) return

        const position = getDropPositionFromRow(row)
        const reordered = reorderRowsWithDropPosition(rows, fromIndex, targetIndex, position)
        if (reordered === rows) return

        reordered.forEach((rowItem, idx) => {
          const { fieldId } = rowItem
          if (!fieldState.value[fieldId]) return
          fieldState.value[fieldId].sortOrder = idx
        })
      }

      return {
        'data-field-id': record.fieldId,
        onDragstart,
        onDragover,
        onDrop,
        onDragenter,
        onDragleave,
        onDragend,
      }
    }

    return {
      isDev,
      selectedSectionState, // eslint-disable-line vue/no-dupe-keys
      sortDocIds,
      anotherScope,
      searchState,
      showSearchText,
      dataSource,
      columns,
      fieldState,
      expandedRowKeys,
      onChange,
      onChangeFieldType,
      onChangeExampleField,
      selectedExampleId, // eslint-disable-line vue/no-dupe-keys
      exampleOptions,
      exampleSelectDropdownStyle,
      getExampleSelectPopupContainer,
      exampleSelectOpen,
      exampleInputRef,
      editingExampleId,
      dropdownVisibleChange,
      onChangeSelectedExample,
      onClickEditExample,
      onClickCopyExample,
      onClickDeleteExample,
      onClickAddExample,
      onBlurExampleInput,
      onChangeExampleInput,
      onFocusExampleInput,
      onClickAddArrayItem,
      onClickRemoveArrayItem,
      onChangeSelectedArrayItemIdx,
      selectedArrayItemIdx,
      onDeleteField,
      getFeldTypeOptions,
      numberTypeOptions,
      customRow,
      onMouseEnterDrag,
      onMouseLeaveDrag,
      findParentFieldData,
      findTopParentFieldData,
      getFieldStateData,
      getExampleListForRecord,
      getExampleScalarForRecord,
      getFieldStateKey,
      getFieldNameInputValue,
      onChangeFieldNameValue,
      onBlurFieldNameInput,
      getEffectiveSelectedDocId,
      setSelectedCvDocId,
      customizedVersionSelectedDocIdByFieldId,
      isExampleFormatError,
      fieldTableRootRef,
      fieldTableRef,
      shouldCheckExampleFormatError,
      getExampleFormatErrorMessage,
    }
  },
}
</script>

<style scoped>
.search-row {
  margin-bottom: 28px;
}

.search-input {
  width: 100%;
}

:deep(.field-table .ant-table-row) {
  background-color: #ffffff;
}

:deep(.field-table .ant-table-row-level-1) {
  background-color: #fbfbfb;
}

:deep(.field-table .ant-table-row-level-2) {
  background-color: #f5f5f5;
}
:deep(.field-table .ant-table-row-level-3) {
  background-color: #f0f0f0;
}

:deep(.field-table .ant-table-row-level-4) {
  background-color: #ebebeb;
}

:deep(.field-table td.ant-table-cell-fix-left) {
  background: inherit;
}

.field-table-example-select :deep(.ant-select-selection-item) {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.example-option-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
}

.example-option-label {
  flex: 1;
  min-width: 0;
  white-space: normal;
  word-break: break-word;
  line-height: 1.4;
}

:deep(.field-table-example-select-dropdown .ant-select-item) {
  height: auto;
  align-items: flex-start;
}

:deep(.field-table-example-select-dropdown .ant-select-item-option-content) {
  flex: 1;
  min-width: 0;
  white-space: normal;
}

:deep(.ant-select-item-option .example-icons) {
  display: none;
  flex-shrink: 0;
}

:deep(.ant-select-item-option:hover .example-icons) {
  display: block;
}

:deep(.field-table .ant-table-row-indent + .ant-table-row-expand-icon) {
  margin: 0;
}

:deep(.example-icons .anticon) {
  margin: 0 2px;
}

:deep(.example-icons .anticon:hover), :deep(.field-table .anticon:hover) {
  color: #1890ff;
}

.array-item-action {
  cursor: pointer;
  color: #bbb;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
}

.array-item-action:hover {
  color: #1890ff;
}

.array-item-actions, .array-items {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.array-item {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.array-item-index {
  flex-shrink: 0;
  color: #bbb;
  line-height: 32px;
}

.example-item-body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.example-item-row {
  display: flex;
  align-items: center;
  width: 100%;
}

.json-example-input-wrap {
  flex: 1;
  min-width: 0;
  width: 100%;
}

.json-example-input-wrap :deep(.ant-input) {
  width: 100%;
}

.table-label {
  display: inline-block;
  color: rgba(0, 0, 0, 0.5);
  font-size: 12px;
  white-space: nowrap;
  min-width: 70px;
}

.docs-setting-desc-list {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.5);
  margin-top: 8px;
  list-style: '- ';
  padding-left: 12px;
  white-space: normal;
  word-break: break-word;
}

.docs-setting-row {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 12px;
}
</style>

<style>
.hide-tooltip {
  display: none;
}
</style>
